import { useEffect } from "react";

const InvoicePrint = ({
  admissionId,
  invoiceNumber,
  billData,
  serviceCharges,
  roomCharges,
  saleTotal,
  hospitalTotal,
  subtotal,
  discountAmt,
  grandTotal,
  payMethod,
}) => {

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "print-style";
    style.innerHTML = `
      @page {
        size: A4;
        margin: 10mm 12mm;
      }

      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Hide everything */
        body * {
          visibility: hidden !important;
        }

        /* Hide browser UI elements */
        nav, header, aside, footer,
        [class*="sidebar"], [class*="topbar"],
        [class*="navbar"], [class*="header"],
        [class*="drawer"], [class*="menu"],
        [id*="sidebar"], [id*="topbar"],
        [id*="navbar"], [id*="header"],
        [id*="drawer"], [id*="menu"] {
          display: none !important;
        }

        /* Show invoice — NO position:fixed, NO min-height */
        #print-invoice {
          visibility: visible !important;
          display: block !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: auto !important;
          background: white !important;
          padding: 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          z-index: 99999 !important;
        }

        #print-invoice * {
          visibility: visible !important;
        }

        /* Force desktop layout on print */
        #print-invoice .sm\\:block {
          display: block !important;
        }
        #print-invoice .sm\\:hidden {
          display: none !important;
        }
        #print-invoice .sm\\:flex-row {
          flex-direction: row !important;
          display: flex !important;
        }
        #print-invoice .sm\\:text-right {
          text-align: right !important;
        }
        #print-invoice .sm\\:w-\\[320px\\] {
          width: 320px !important;
          margin-left: auto !important;
        }
        #print-invoice .sm\\:text-\\[13px\\] {
          font-size: 13px !important;
        }
        #print-invoice .sm\\:text-\\[22px\\] {
          font-size: 22px !important;
        }

        /* Clean table styles */
        #print-invoice table {
          width: 100% !important;
          border-collapse: collapse !important;
          table-layout: auto !important;
        }
        #print-invoice table th,
        #print-invoice table td {
          padding: 6px 10px !important;
          font-size: 11px !important;
          white-space: nowrap !important;
          border: 1px solid #d1d5db !important;
        }
        #print-invoice table thead {
          background-color: #eff6ff !important;
        }

        /* Prevent page breaks inside tables */
        #print-invoice tr {
          page-break-inside: avoid !important;
        }
      }
    `;

    document.head.appendChild(style);
    return () => document.getElementById("print-style")?.remove();
  }, []);

  return (
    <div id="print-invoice">
      <div className="font-sans text-[12px] sm:text-[13px]">

        
        <div className="flex flex-col sm:flex-row justify-between border-b-2 border-[#215187] pb-4 mb-6">
          <div>
            <h1 className="text-[20px] sm:text-[22px] font-bold text-[#215187]">
              Patient Invoice
            </h1>
            <p className="text-gray-600">
              Invoice #: <strong>{invoiceNumber || "Pending"}</strong>
            </p>
            <p className="text-gray-600">Admission ID: {admissionId}</p>
            <p className="text-gray-600">Patient: {billData?.full_name}</p>
          </div>
          <div className="sm:text-right text-gray-600">
            <p>Date: {new Date().toLocaleDateString()}</p>
            {billData?.days_stayed != null && (
              <p>Days Stayed: {billData.days_stayed}</p>
            )}
          </div>
        </div>

      
        {serviceCharges.length > 0 && (
          <div className="mb-6">
            <h3 className="text-[#215187] font-semibold uppercase mb-3">
              Medical Services
            </h3>

            
            <div className="hidden sm:block">
              <table className="w-full border text-[12px]">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border p-2 text-left">Service</th>
                    <th className="border p-2 text-center">Qty</th>
                    <th className="border p-2 text-right">Unit Price</th>
                    <th className="border p-2 text-right">Total</th>
                    <th className="border p-2 text-center">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceCharges.map((c, i) => (
                    <tr key={i}>
                      <td className="border p-2">{c.service_name}</td>
                      <td className="border p-2 text-center">{c.quantity}</td>
                      <td className="border p-2 text-right">
                        Rs. {Number(c.unit_price).toLocaleString()}
                      </td>
                      <td className="border p-2 text-right font-semibold">
                        Rs. {Number(c.total_charge).toLocaleString()}
                      </td>
                      <td className="border p-2 text-center text-gray-500">
                        {c.service_datetime?.slice(0, 10)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-semibold text-[#215187]">
                    <td colSpan={3} className="border p-2">Services Subtotal</td>
                    <td colSpan={2} className="border p-2 text-right">
                      Rs. {saleTotal.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

        
            <div className="sm:hidden space-y-3">
              {serviceCharges.map((c, i) => (
                <div key={i} className="border rounded-xl p-3 shadow-sm bg-white">
                  <div className="flex justify-between">
                    <p className="font-semibold text-[#215187]">{c.service_name}</p>
                    <span className="text-[11px] text-gray-500">
                      {c.service_datetime?.slice(0, 10)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                    <div>
                      <p className="text-gray-400">Qty</p>
                      <p>{c.quantity}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Unit Price</p>
                      <p>Rs. {Number(c.unit_price).toLocaleString()}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-400">Total</p>
                      <p className="font-semibold text-[#215187]">
                        Rs. {Number(c.total_charge).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex justify-between border rounded-xl p-3 bg-blue-50 font-semibold text-[#215187]">
                <span>Services Subtotal</span>
                <span>Rs. {saleTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

      
        {roomCharges.length > 0 && (
          <div className="mb-6">
            <h3 className="text-[#215187] font-semibold uppercase mb-3">
              Hospital / Room Charges
            </h3>

            <div className="hidden sm:block">
              <table className="w-full border text-[12px]">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="border p-2 text-left">Room</th>
                    <th className="border p-2 text-center">Date</th>
                    <th className="border p-2 text-right">Rate</th>
                    <th className="border p-2 text-right">Discount</th>
                    <th className="border p-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {roomCharges.map((c, i) => (
                    <tr key={i}>
                      <td className="border p-2">{c.room_name}</td>
                      <td className="border p-2 text-center text-gray-500">
                        {c.charge_date?.slice(0, 10)}
                      </td>
                      <td className="border p-2 text-right">
                        Rs. {Number(c.rate_per_day).toLocaleString()}
                      </td>
                      <td className="border p-2 text-right">
                        Rs. {Number(c.discount_applied).toLocaleString()}
                      </td>
                      <td className="border p-2 text-right font-semibold">
                        Rs. {Number(c.total_charge).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-semibold text-[#215187]">
                    <td colSpan={4} className="border p-2">Hospital Subtotal</td>
                    <td className="border p-2 text-right">
                      Rs. {hospitalTotal.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          
            <div className="sm:hidden space-y-3">
              {roomCharges.map((c, i) => (
                <div key={i} className="border rounded-xl p-3 shadow-sm bg-white">
                  <div className="flex justify-between">
                    <p className="font-semibold text-[#215187]">{c.room_name}</p>
                    <span className="text-[11px] text-gray-500">
                      {c.charge_date?.slice(0, 10)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
                    <div>
                      <p className="text-gray-400">Rate</p>
                      <p>Rs. {Number(c.rate_per_day).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Discount</p>
                      <p>Rs. {Number(c.discount_applied).toLocaleString()}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-400">Total</p>
                      <p className="font-semibold text-[#215187]">
                        Rs. {Number(c.total_charge).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex justify-between border rounded-xl p-3 bg-blue-50 font-semibold text-[#215187]">
                <span>Hospital Subtotal</span>
                <span>Rs. {hospitalTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

      
        <div className="w-full sm:w-[320px] ml-auto border rounded-xl overflow-hidden">
          <div className="flex justify-between p-3 bg-gray-100">
            <span>Subtotal</span>
            <span>Rs. {subtotal.toLocaleString()}</span>
          </div>
          {discountAmt > 0 && (
            <div className="flex justify-between p-3 bg-green-50 text-green-700">
              <span>Discount</span>
              <span>− Rs. {discountAmt.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between p-4 bg-[#215187] text-white">
            <span className="uppercase text-[11px] font-semibold">
              Grand Total
            </span>
            <span className="text-[16px] font-bold">
              Rs. {grandTotal.toLocaleString()}
            </span>
          </div>
        </div>

        
        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-[12px] text-gray-600">
          <span className="font-semibold">Payment Method: </span>
          <span className="capitalize">{payMethod?.replace("_", " ")}</span>
        </div>
        <div className="text-center text-[11px] text-gray-400 mt-6 border-t pt-3">
          This is a computer-generated invoice. No signature required.
        </div>

      </div>
    </div>
  );
};

export default InvoicePrint;